/**
 * Диалоги карточки сервиса: заведение, пополнение, сверка, ручной расход,
 * параметры подписки (docs/08-screens.md).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Icon, Input, Select } from '@/ds';
import { DateField, MoneyField } from '@/components/fields';
import { Money, Provenance } from '@/components/money';
import { ApiError } from '@/api/client';
import {
  useOperation,
  useRatePreview,
  useWallets,
  type BillingModel,
  type Provider,
  type ServiceDetail,
  type ServiceSummary,
} from '@/api/hooks';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import {
  dateInputToIso,
  minorToInput,
  parseMinor,
  parseRate,
  rateToInput,
  todayInputValue,
} from '@/lib/money';
import { useControlSize } from '@/lib/useControlSize';

const PROVIDERS = [
  { value: 'manual', label: 'Без интеграции, вручную' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'digitalocean', label: 'DigitalOcean' },
  { value: 'google_workspace', label: 'Google Workspace' },
];

const BILLING_MODELS = [
  { value: 'prepaid_balance', label: 'Предоплаченный баланс' },
  { value: 'postpaid_invoice', label: 'Постоплата по счетам' },
  { value: 'subscription', label: 'Подписка' },
];

const CURRENCIES = [
  { value: 'USD', label: 'Доллары' },
  { value: 'EUR', label: 'Евро' },
  { value: 'RUB', label: 'Рубли' },
];

function errorText(...errors: unknown[]): string | null {
  for (const error of errors) {
    if (error instanceof ApiError) return error.message;
  }
  return null;
}

// --------------------------------------------------------- заведение сервиса

export function ServiceFormDialog({
  service,
  open,
  onClose,
}: {
  service?: ServiceSummary | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const wallets = useWallets();
  const create = useOperation<{
    name: string;
    provider: Provider;
    billingModel: BillingModel;
    currency: string;
    defaultWalletId: string | null;
    forecastWindowDays: number;
  }>('service.create');
  const update = useOperation<{
    id: string;
    name: string;
    defaultWalletId: string | null;
    forecastWindowDays: number;
    balanceUrl: string | null;
  }>('service.update');

  const [name, setName] = useState('');
  const [provider, setProvider] = useState<Provider>('manual');
  const [billingModel, setBillingModel] = useState<BillingModel>('prepaid_balance');
  const [currency, setCurrency] = useState('USD');
  const [walletId, setWalletId] = useState('');
  const [windowDays, setWindowDays] = useState('7');
  const [balanceUrl, setBalanceUrl] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(service?.name ?? '');
    setProvider(service?.provider ?? 'manual');
    setBillingModel(service?.billingModel ?? 'prepaid_balance');
    setCurrency(service?.currency ?? 'USD');
    setWalletId(service?.defaultWalletId ?? '');
    setWindowDays(String(service?.forecastWindowDays ?? 7));
    // В поле лежит только свой адрес: подставленный адаптером виден подсказкой.
    setBalanceUrl(service?.balanceUrlIsCustom ? (service.balanceUrl ?? '') : '');
    setLocalError(null);
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service?.id]);

  // Платить можно только с кошелька в валюте сервиса.
  const walletOptions = useMemo(() => {
    const matching = (wallets.data?.wallets ?? []).filter(
      (w) => w.currency === currency && w.isActive,
    );
    return [
      { value: '', label: 'Не выбран' },
      ...matching.map((w) => ({ value: w.id, label: w.name })),
    ];
  }, [wallets.data, currency]);

  const pending = create.isPending || update.isPending;
  const serverError = errorText(create.error, update.error);

  function submit() {
    setLocalError(null);
    if (name.trim().length === 0) {
      setLocalError('Название не может быть пустым');
      return;
    }
    const days = Number(windowDays);
    if (!Number.isInteger(days) || days < 1 || days > 90) {
      setLocalError('Окно прогноза задаётся числом от 1 до 90 дней');
      return;
    }

    const trimmedUrl = balanceUrl.trim();
    if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
      setLocalError('Адрес кабинета начинается с http:// или https://');
      return;
    }

    if (service) {
      update.mutate(
        {
          id: service.id,
          name: name.trim(),
          defaultWalletId: walletId || null,
          forecastWindowDays: days,
          balanceUrl: trimmedUrl || null,
        },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        {
          name: name.trim(),
          provider,
          billingModel,
          currency,
          defaultWalletId: walletId || null,
          forecastWindowDays: days,
        },
        { onSuccess: onClose },
      );
    }
  }

  return (
    <Dialog
      open={open}
      title={service ? 'Изменить сервис' : 'Новый сервис'}
      description={
        service
          ? 'Тип биллинга и валюта не меняются: от них зависят прошлые расчёты'
          : 'Внешний платный сервис. Ключ доступа задаётся после создания, в карточке сервиса'
      }
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={pending}>
            {service ? 'Сохранить' : 'Создать'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <Input
          size={size}
          label="Название"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="OpenAI (Whisper)"
        />

        {service ? null : (
          <>
            <Select
              size={size}
              label="Провайдер"
              options={PROVIDERS}
              value={provider}
              onChange={(e) => setProvider(e.target.value as Provider)}
              help="У OpenAI, Anthropic и DigitalOcean расход собирается автоматически, остальное вводится руками"
            />
            <Select
              size={size}
              label="Тип биллинга"
              options={BILLING_MODELS}
              value={billingModel}
              onChange={(e) => setBillingModel(e.target.value as BillingModel)}
              help="Определяет, что показывается в карточке: баланс, счета или дата списания"
            />
            <Select
              size={size}
              label="Валюта"
              options={CURRENCIES}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            />
          </>
        )}

        <Select
          size={size}
          label="Кошелёк по умолчанию"
          options={walletOptions}
          value={walletId}
          onChange={(e) => setWalletId(e.target.value)}
          help={`Показаны только кошельки в ${currency}`}
        />

        {billingModel === 'prepaid_balance' ? (
          <Input
            size={size}
            label="Окно прогноза, дней"
            type="number"
            value={windowDays}
            onChange={(e) => setWindowDays(e.target.value)}
            help="По скольким последним дням считается средний расход. По умолчанию 7"
          />
        ) : null}

        {service ? (
          <Input
            size={size}
            label="Страница баланса в кабинете"
            type="url"
            value={balanceUrl}
            onChange={(e) => setBalanceUrl(e.target.value)}
            placeholder={service.balanceUrlIsCustom ? '' : (service.balanceUrl ?? 'https://')}
            help={
              service.balanceUrlIsCustom
                ? 'Свой адрес. Очистите поле, чтобы вернуть адрес по умолчанию'
                : service.balanceUrl
                  ? 'Пусто — открывается адрес провайдера из подсказки'
                  : 'У этого провайдера страницы по умолчанию нет'
            }
          />
        ) : null}

        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------- пополнение сервиса

export function ServiceTopUpDialog({
  service,
  open,
  onClose,
}: {
  service: ServiceSummary;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const wallets = useWallets();
  const create = useOperation<
    {
      serviceId: string;
      walletId: string | null;
      occurredAt: string;
      amount: number;
      fee: number;
      manualRate: number | null;
      comment?: string;
    },
    { id: string; rubCost: number | null }
  >('service_topup.create');

  const [occurredAt, setOccurredAt] = useState(todayInputValue());
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [walletId, setWalletId] = useState(service.defaultWalletId ?? '');
  const [manualRate, setManualRate] = useState(false);
  const [rate, setRate] = useState('');
  const [comment, setComment] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const matchingWallets = (wallets.data?.wallets ?? []).filter(
    (w) => w.currency === service.currency && w.isActive,
  );
  const selectedWallet = matchingWallets.find((w) => w.id === walletId) ?? null;

  // Курс считает сервер по правилу кошелька (docs/04-finance.md): здесь
  // только подсказка до сохранения.
  const preview = useRatePreview(walletId || null, occurredAt, open && !manualRate);
  const previewRate = preview.data?.rate ?? null;

  useEffect(() => {
    if (!open) return;
    setOccurredAt(todayInputValue());
    setAmount('');
    setWalletId(service.defaultWalletId ?? '');
    setManualRate(false);
    setRate('');
    setComment('');
    setLocalError(null);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service.id]);

  // Комиссия кошелька подставляется из его описания: у Flowbit $0,25 за оплату.
  useEffect(() => {
    if (!open) return;
    setFee(selectedWallet && selectedWallet.defaultFeeMinor > 0 ? minorToInput(selectedWallet.defaultFeeMinor) : '');
  }, [open, selectedWallet?.id, selectedWallet?.defaultFeeMinor]);

  useEffect(() => {
    if (manualRate && rate === '' && previewRate) {
      setRate(rateToInput(previewRate.rateE4 / 10_000));
    }
  }, [manualRate, rate, previewRate]);

  const amountMinor = parseMinor(amount);
  const feeMinor = fee.trim() === '' ? 0 : parseMinor(fee);
  const rateMinor = parseRate(rate);
  const appliedRate = manualRate
    ? rateMinor === null
      ? null
      : rateMinor / 100
    : previewRate
      ? previewRate.rateE4 / 10_000
      : null;
  const estimatedCost =
    amountMinor !== null && feeMinor !== null && appliedRate !== null
      ? Math.round((amountMinor + feeMinor) * appliedRate)
      : null;

  function rateExplanation(): string {
    if (manualRate) {
      return appliedRate === null
        ? 'Укажите курс'
        : `По курсу ${formatNumber(appliedRate, 2)} ₽, введён вручную`;
    }
    if (preview.isLoading) return 'Считаем курс';
    if (!previewRate) return 'Курса на эту дату нет: задайте его вручную или сохраните без себестоимости';
    const value = formatNumber(previewRate.rateE4 / 10_000, 2);
    switch (previewRate.source) {
      case 'p2p':
        return `По курсу P2P Bybit ${value} ₽, снимок ${formatDateTime(previewRate.at)}`;
      case 'cbr':
        return `По курсу ЦБ ${value} ₽ на ${formatDate(previewRate.at)}: снимка P2P на эту дату нет`;
      default:
        return `По курсу пополнений кошелька ${value} ₽ на дату операции`;
    }
  }

  const serverError = errorText(create.error);

  function submit() {
    setLocalError(null);
    const iso = dateInputToIso(occurredAt);
    if (!iso) {
      setLocalError('Укажите дату пополнения');
      return;
    }
    if (amountMinor === null || amountMinor <= 0) {
      setLocalError('Укажите сумму пополнения');
      return;
    }
    if (manualRate && rateMinor === null) {
      setLocalError('Укажите курс или снимите ручной ввод');
      return;
    }
    if (feeMinor === null || feeMinor < 0) {
      setLocalError('Комиссия — сумма в валюте кошелька или пусто');
      return;
    }

    create.mutate(
      {
        serviceId: service.id,
        walletId: walletId || null,
        occurredAt: iso,
        amount: amountMinor,
        fee: feeMinor,
        manualRate: manualRate ? rateMinor : null,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open={open}
      title="Пополнить сервис"
      description={`Оплата в пользу «${service.name}» в ${service.currency}`}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={create.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 'var(--space-5)',
          }}
        >
          <DateField label="Дата" value={occurredAt} onChange={setOccurredAt} />
          <MoneyField
            label={`Сумма, ${service.currency}`}
            value={amount}
            onChange={setAmount}
            autoFocus
          />
          <MoneyField
            label="Комиссия кошелька"
            value={fee}
            onChange={setFee}
            help="Сверх суммы, если есть"
          />
        </div>

        <Select
          size={size}
          label="Кошелёк-источник"
          options={[
            { value: '', label: 'Не указан' },
            ...matchingWallets.map((w) => ({ value: w.id, label: w.name })),
          ]}
          value={walletId}
          onChange={(e) => setWalletId(e.target.value)}
          help={
            matchingWallets.length === 0
              ? `Кошельков в ${service.currency} пока нет`
              : `Показаны только кошельки в ${service.currency}`
          }
        />

        <div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              font: '400 13px var(--font-text)',
              color: 'var(--text-body)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={manualRate}
              onChange={(e) => setManualRate(e.target.checked)}
            />
            Задать курс вручную
          </label>
          {manualRate ? (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <MoneyField
                label={`Курс, ₽ за 1 ${service.currency}`}
                value={rate}
                onChange={setRate}
              />
            </div>
          ) : null}
        </div>

        <div
          style={{
            background: 'var(--surface-sunken)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-5)',
          }}
        >
          <Provenance>Себестоимость в рублях, вместе с комиссией</Provenance>
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Money minor={estimatedCost} currency="RUB" />
          </div>
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Provenance>{rateExplanation()}</Provenance>
          </div>
        </div>

        <Input
          size={size}
          label="Комментарий"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          help="Необязательно"
        />

        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// -------------------------------------------------------------------- сверка

export function ReconcileDialog({
  service,
  open,
  onClose,
}: {
  service: ServiceDetail;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const reconcile = useOperation<
    { serviceId: string; actualBalance: number; comment?: string },
    { id: string; calculatedBalance: number; delta: number }
  >('balance_adjustment.create');

  const [actual, setActual] = useState('');
  const [comment, setComment] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setActual(minorToInput(service.balanceMinor));
    setComment('');
    setLocalError(null);
    reconcile.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service.id]);

  const calculated = service.balanceMinor ?? 0;
  const actualMinor = parseMinor(actual);
  const delta = actualMinor === null ? null : actualMinor - calculated;
  const large =
    delta !== null &&
    (calculated === 0 ? delta !== 0 : Math.abs(delta) > Math.abs(calculated) * 0.1);

  const serverError = errorText(reconcile.error);

  function submit() {
    setLocalError(null);
    if (actualMinor === null) {
      setLocalError('Укажите фактический остаток из кабинета сервиса');
      return;
    }
    reconcile.mutate(
      {
        serviceId: service.id,
        actualBalance: actualMinor,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open={open}
      title="Сверить баланс"
      description="Введите остаток из личного кабинета сервиса. Приложение запишет корректировку и дальше будет считать от нового значения"
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={reconcile.isPending}>
            Записать сверку
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div
          style={{
            background: 'var(--surface-sunken)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-5)',
          }}
        >
          <Provenance>Расчётный остаток</Provenance>
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Money minor={calculated} currency={service.currency} size="lg" />
          </div>
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Provenance>Пополнения минус расход плюс прошлые корректировки</Provenance>
          </div>
        </div>

        {service.balanceUrl ? (
          <div>
            <Button
              variant="secondary"
              size={size}
              as="a"
              href={service.balanceUrl}
              iconRight={<Icon name="ExternalLink" size={15} />}
            >
              Открыть кабинет провайдера
            </Button>
            <div style={{ marginTop: 'var(--space-2)' }}>
              <Provenance>
                Откроется страница с настоящим остатком. Перенесите цифру оттуда сюда.
              </Provenance>
            </div>
          </div>
        ) : null}

        <MoneyField
          label={`Фактический остаток, ${service.currency}`}
          value={actual}
          onChange={setActual}
          autoFocus
        />

        {delta !== null && delta !== 0 ? (
          <div>
            <Provenance>Разница</Provenance>
            <div style={{ marginTop: 'var(--space-2)' }}>
              <Money
                minor={delta}
                currency={service.currency}
                tone={large ? 'warning' : 'normal'}
              />
            </div>
            {large ? (
              <div style={{ marginTop: 'var(--space-2)' }}>
                <span
                  style={{
                    font: '400 13px/1.5 var(--font-text)',
                    color: 'var(--status-warning-fg)',
                  }}
                >
                  Разница больше десятой части баланса. Проверьте, все ли пополнения внесены
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        <Input
          size={size}
          label="Комментарий"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          help="Необязательно: чем объясняется расхождение"
        />

        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------- ручной расход

export function ManualUsageDialog({
  service,
  open,
  onClose,
}: {
  service: ServiceSummary;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const record = useOperation<
    { serviceId: string; date: string; amount: number; note?: string },
    { id: string; replaced: boolean }
  >('usage.create_manual');

  const [date, setDate] = useState(todayInputValue());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDate(todayInputValue());
    setAmount('');
    setNote('');
    setLocalError(null);
    record.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service.id]);

  const amountMinor = parseMinor(amount);
  const serverError = errorText(record.error);

  function submit() {
    setLocalError(null);
    const iso = dateInputToIso(date);
    if (!iso) {
      setLocalError('Укажите день, к которому относится расход');
      return;
    }
    if (amountMinor === null || amountMinor <= 0) {
      setLocalError('Укажите сумму расхода');
      return;
    }
    record.mutate(
      {
        serviceId: service.id,
        date: iso,
        amount: amountMinor,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open={open}
      title="Внести расход"
      description={`Списание сервиса «${service.name}» за один день`}
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={record.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <DateField label="День" value={date} onChange={setDate} />
        <MoneyField
          label={`Сумма, ${service.currency}`}
          value={amount}
          onChange={setAmount}
          autoFocus
        />
        <Input
          size={size}
          label="Разрез"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          help="Необязательно: модель, тип операции. Расход с тем же разрезом за тот же день заменяется"
        />
        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- подписка

export function SubscriptionDialog({
  service,
  open,
  onClose,
}: {
  service: ServiceSummary;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const save = useOperation<{
    serviceId: string;
    amount: number;
    period: 'monthly' | 'yearly';
    nextChargeAt: string;
    seats: number | null;
  }>('subscription.set');

  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [nextChargeAt, setNextChargeAt] = useState(todayInputValue());
  const [seats, setSeats] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAmount(minorToInput(service.subscription?.amount ?? null));
    setPeriod(service.subscription?.period ?? 'monthly');
    setNextChargeAt(
      service.subscription?.nextChargeAt.slice(0, 10) ?? todayInputValue(),
    );
    setSeats(service.subscription?.seats ? String(service.subscription.seats) : '');
    setLocalError(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service.id]);

  const amountMinor = parseMinor(amount);
  const serverError = errorText(save.error);

  function submit() {
    setLocalError(null);
    const iso = dateInputToIso(nextChargeAt);
    if (!iso) {
      setLocalError('Укажите дату следующего списания');
      return;
    }
    if (amountMinor === null || amountMinor <= 0) {
      setLocalError('Укажите сумму списания');
      return;
    }
    const seatCount = seats.trim() === '' ? null : Number(seats);
    if (seatCount !== null && (!Number.isInteger(seatCount) || seatCount <= 0)) {
      setLocalError('Число мест задаётся целым числом');
      return;
    }

    save.mutate(
      {
        serviceId: service.id,
        amount: amountMinor,
        period,
        nextChargeAt: iso,
        seats: seatCount,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open={open}
      title="Параметры подписки"
      description={`Сумма и цикл списания для «${service.name}»`}
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={save.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <MoneyField
          label={`Сумма списания, ${service.currency}`}
          value={amount}
          onChange={setAmount}
          autoFocus
        />
        <Select
          size={size}
          label="Период"
          options={[
            { value: 'monthly', label: 'Раз в месяц' },
            { value: 'yearly', label: 'Раз в год' },
          ]}
          value={period}
          onChange={(e) => setPeriod(e.target.value as 'monthly' | 'yearly')}
        />
        <DateField
          label="Следующее списание"
          value={nextChargeAt}
          onChange={setNextChargeAt}
          help="В этот день приложение создаст запись расхода и сдвинет дату"
        />
        <Input
          size={size}
          label="Мест"
          type="number"
          value={seats}
          onChange={(e) => setSeats(e.target.value)}
          help="Необязательно"
        />
        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
